import api from "@/lib/api";

/** Előnézeti kocka JPEG base64 (kisebb = gyorsabb AI). */
export function grabVideoJpegBase64(video, maxWidth = 640) {
  if (!video?.videoWidth || !video?.videoHeight) return null;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const scale = Math.min(1, maxWidth / vw);
  const w = Math.max(1, Math.round(vw * scale));
  const h = Math.max(1, Math.round(vh * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(video, 0, 0, w, h);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.72);
  return dataUrl.split(",")[1] || null;
}

export async function runLivePhotoCheck(video, { angle } = {}) {
  const b64 = grabVideoJpegBase64(video);
  if (!b64) return null;
  const r = await api.post("/ai/fit-check", {
    image_base64: b64,
    mode: "live",
    angle: angle || undefined,
  });
  return r.data;
}

const LIGHT_HU = {
  ok: "Megfelelő fény",
  shadowy: "Kissé árnyékos",
  too_dark: "Túl árnyékos",
  overexposed: "Túl erős fény",
};

/** Felhasználónak olvasható AI státusz a kamera sávhoz. */
export function getCameraCoachUi(coach, { loading, offline, angleLabel, advisoryOnly } = {}) {
  if (loading && !coach) {
    return {
      tone: "neutral",
      car: "AI ellenőrzés",
      light: "",
      detail: "Kép elemzése folyamatban…",
      badge: "…",
    };
  }
  if (!coach && offline) {
    return {
      tone: "neutral",
      car: "AI nem elérhető",
      light: "",
      detail: "Fotózható, de nincs élő ellenőrzés (backend / bejelentkezés).",
      badge: "—",
    };
  }
  if (!coach) return null;

  const car =
    coach.fit === "none"
      ? "Autó nem észlelhető"
      : coach.fit === "adjust"
        ? "Autó észlelve — igazítás kell"
        : "Autó észlelve";

  const light = LIGHT_HU[coach.lighting] || "";

  if (coach.can_shoot === false) {
    const detail =
      coach.lighting_message
      || (coach.lighting === "too_dark"
        ? (advisoryOnly
          ? "Túl árnyékos — érdemes világosabb helyre menni, de a felvételt indíthatod."
          : "Túl árnyékos — nem készíthető el a fotó. Menj világosabb helyre.")
        : (advisoryOnly
          ? "Túl erős a fény — érdemes árnyékba menni, de a felvételt indíthatod."
          : "Túl erős a fény — nem készíthető el a fotó."));
    return {
      tone: advisoryOnly ? "warn" : "block",
      car,
      light,
      detail,
      badge: advisoryOnly ? "Tipp" : "Tiltva",
    };
  }

  if (coach.fit === "none") {
    return {
      tone: "warn",
      car,
      light,
      detail: coach.advice || "Tartsd az autót a kép közepén, forgasd a kamerát felé.",
      badge: "Figyelem",
    };
  }

  if (coach.fit === "adjust") {
    const tip = coach.advice || "Lépj közelebb, vagy igazítsd a szöget.";
    const shadow = coach.lighting === "shadowy" && coach.lighting_message
      ? ` ${coach.lighting_message}`
      : "";
    return {
      tone: "warn",
      car,
      light,
      detail: `${tip}${shadow}`.trim(),
      badge: "Igazítás",
    };
  }

  if (coach.lighting === "shadowy") {
    return {
      tone: "warn",
      car,
      light,
      detail: coach.lighting_message || coach.advice || "Fotózható, de lehetne világosabb is.",
      badge: "Árnyék",
    };
  }

  const angleHint = angleLabel ? ` (${angleLabel})` : "";
  return {
    tone: "ok",
    car,
    light: light || "Megfelelő fény",
    detail: coach.advice || `Minden rendben — fotózható${angleHint}.`,
    badge: "Fotózható",
  };
}

export const CAMERA_COACH_STYLES = {
  ok: {
    box: "bg-emerald-500/10 border-emerald-400/30",
    icon: "text-emerald-400",
    title: "text-emerald-200",
    light: "text-emerald-300/80",
    detail: "text-slate-400",
    badge: "text-emerald-300",
  },
  warn: {
    box: "bg-amber-500/10 border-amber-400/30",
    icon: "text-amber-400",
    title: "text-amber-200",
    light: "text-amber-300/80",
    detail: "text-slate-400",
    badge: "text-amber-200",
  },
  block: {
    box: "bg-red-500/10 border-red-400/30",
    icon: "text-red-400",
    title: "text-red-200",
    light: "text-red-300/80",
    detail: "text-slate-400",
    badge: "text-red-300",
  },
  neutral: {
    box: "bg-white/[0.03] border-white/10",
    icon: "text-electric",
    title: "text-slate-200",
    light: "text-slate-500",
    detail: "text-slate-500",
    badge: "text-slate-400",
  },
};
